// src/components/NurseActivityTracker.tsx
import React, { useState, useEffect, memo } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './NurseActivityTracker.css';

interface NurseStats {
  patientsCount: number;
  activitiesEntered: number;
  handoffsCompleted: number;
  totalTimeInApp: number;
  reportsSubmitted: number;
  otherRecordingsSubmitted: number;
}

interface NurseActivityTrackerProps {
  shiftStartTime: Date;
  nurseStats: NurseStats;
  shiftDuration?: number; // in hours, default 12
}

const NurseActivityTracker: React.FC<NurseActivityTrackerProps> = memo(({
  shiftStartTime,
  nurseStats,
  shiftDuration = 12
}) => {
  const { theme } = useTheme();
  // Initialize with calculated elapsed time to avoid 1-second delay on refresh
  const [elapsedTime, setElapsedTime] = useState(() => {
    const now = new Date();
    return Math.floor((now.getTime() - shiftStartTime.getTime()) / 1000);
  });
  const [isExpanded, setIsExpanded] = useState(true);

  // Update elapsed time every second
  useEffect(() => {
    // Immediately update in case shiftStartTime changed
    const now = new Date();
    setElapsedTime(Math.floor((now.getTime() - shiftStartTime.getTime()) / 1000));
    
    const interval = setInterval(() => {
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - shiftStartTime.getTime()) / 1000);
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [shiftStartTime]);

  // Calculate shift progress percentage
  const shiftDurationSeconds = shiftDuration * 60 * 60;
  const progressPercentage = Math.min((elapsedTime / shiftDurationSeconds) * 100, 100);

  // Format time as HH:MM:SS
  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format remaining time
  const remainingSeconds = Math.max(shiftDurationSeconds - elapsedTime, 0);
  const formatRemainingTime = (): string => {
    const hrs = Math.floor(remainingSeconds / 3600);
    const mins = Math.floor((remainingSeconds % 3600) / 60);
    if (hrs > 0) {
      return `${hrs}h ${mins}m remaining`;
    }
    return `${mins}m remaining`;
  };

  const isDark = theme === 'dark';

  return (
    <div className={`nurse-activity-tracker ${isDark ? 'dark' : ''}`}>
      <div 
        className="tracker-header" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="tracker-title">
          <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
            <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
          </svg>
          <span>Shift Activity</span>
        </div>
        <svg 
          className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
          viewBox="0 0 24 24" 
          fill="currentColor" 
          width="18" 
          height="18"
        >
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
        </svg>
      </div>

      {isExpanded && (
        <div className="tracker-content">
          {/* Shift Progress Bar */}
          <div className="shift-progress-section">
            <div className="progress-header">
              <span className="progress-label">Shift Progress</span>
              <span className="progress-time">{formatTime(elapsedTime)}</span>
            </div>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{ width: `${progressPercentage}%` }}
              />
              <div className="progress-markers">
                <span className="marker" style={{ left: '25%' }} />
                <span className="marker" style={{ left: '50%' }} />
                <span className="marker" style={{ left: '75%' }} />
              </div>
            </div>
            <div className="progress-footer">
              <span className="shift-start">
                {shiftStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="shift-remaining">{formatRemainingTime()}</span>
            </div>
          </div>

          {/* Activity Stats Grid */}
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon patients">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                </svg>
              </div>
              <div className="stat-details">
                <span className="stat-value">{nurseStats.patientsCount}</span>
                <span className="stat-label">Patients</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon activities">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                </svg>
              </div>
              <div className="stat-details">
                <span className="stat-value">{nurseStats.activitiesEntered}</span>
                <span className="stat-label">Activities</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon handoffs">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <div className="stat-details">
                <span className="stat-value">{nurseStats.handoffsCompleted}</span>
                <span className="stat-label">Handoffs</span>
              </div>
            </div>

            <div className="stat-item">
              <div className="stat-icon reports">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                </svg>
              </div>
              <div className="stat-details">
                <span className="stat-value">{nurseStats.reportsSubmitted}</span>
                <span className="stat-label">Reports</span>
              </div>
            </div>

            <div className="stat-item stat-item-full">
              <div className="stat-icon recordings">
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1.2-9.1c0-.66.54-1.2 1.2-1.2.66 0 1.2.54 1.2 1.2l-.01 6.2c0 .66-.53 1.2-1.19 1.2-.66 0-1.2-.54-1.2-1.2V4.9zm6.5 6.1c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
                </svg>
              </div>
              <div className="stat-details">
                <span className="stat-value">{nurseStats.otherRecordingsSubmitted}</span>
                <span className="stat-label">Other Interactions</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

NurseActivityTracker.displayName = 'NurseActivityTracker';

export default NurseActivityTracker;
